import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BellRing,
  CalendarDays,
  Check,
  History as HistoryIcon,
  ListChecks,
  Mail,
  NotebookPen,
  PanelRightClose,
  PanelRightOpen,
  Pin,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { PRIORITIES } from '@gbv/types';
import { formatIstTime, istToday, relativeTime } from '@gbv/utils';
import { Button, Input, Modal, Select, Skeleton, Textarea, cn, useToast } from '@gbv/ui';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../stores/auth';

/**
 * The "Internal update" rail — GoBeeVir's own work, in one fixed column.
 *
 * Two properties define it:
 *
 *   1. It is INTERNAL. Every figure comes from a table with no orgId, so no
 *      client's data can appear here.
 *   2. It is FIXED. Because the request takes no org id, switching client
 *      cannot change a single row. The working area changes; this does not.
 *
 * Every card is a window onto a full page in the Internal half of the nav —
 * the same data, the same tables — so something created there shows here, and
 * clicking here opens there. One place, two views.
 *
 * Dark on purpose: it and the nav are the two fixed edges of the app, and the
 * working area between them is the only part that moves.
 */

interface RailTask {
  id: string;
  title: string;
  priority: string;
  dueAt: string | null;
  isOverdue: boolean;
}

interface InternalRail {
  tasks: RailTask[];
  events: Array<{ id: string; title: string; startsAt: string; location: string | null }>;
  notes: Array<{ id: string; title: string; isPinned: boolean; updatedAt: string }>;
  mails: Array<{
    id: string;
    title: string;
    body: string;
    link: string | null;
    isRead: boolean;
    createdAt: string;
  }>;
  reminders: Array<{
    id: string;
    title: string;
    remindAt: string;
    isDue: boolean;
  }>;
  calendarDays: string[];
  counts: { openTasks: number; myTasks: number; unreadMails: number; dueReminders: number };
}

interface TeamMate {
  id: string;
  name: string;
}

const PRIORITY_DOTS: Record<string, string> = {
  URGENT: 'bg-danger-mid',
  HIGH: 'bg-bee',
  MEDIUM: 'bg-vir',
  LOW: 'bg-white/25',
};

const COLLAPSE_KEY = 'gbv.rail.collapsed';

export function InternalUpdateRail() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const userId = useAuth((s) => s.user?.id);

  const [addingTask, setAddingTask] = useState(false);
  const [addingReminder, setAddingReminder] = useState(false);
  const [pinging, setPinging] = useState(false);

  /**
   * Collapsed state is remembered per browser.
   *
   * Read lazily from localStorage rather than in an effect, so the rail does
   * not render open for a frame and then snap shut on every page load — which
   * reads as a glitch and shifts the working area under the cursor.
   */
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      // Private mode and some embedded browsers throw on access.
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* Not worth surfacing: the rail still works, it just forgets. */
    }
  }, [collapsed]);

  // No orgId in the key and none in the request: this column is the same for
  // every client, which is the entire contract.
  const { data, isLoading } = useQuery({
    queryKey: ['internal-rail'],
    queryFn: () => api.get<InternalRail>('/internal/rail'),
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['internal-rail'] });
    // The full pages read the same tables, so they must refresh too.
    void queryClient.invalidateQueries({ queryKey: ['internal'] });
  };

  const completeTask = useMutation({
    mutationFn: (id: string) => api.patch(`/internal/tasks/${id}`, { status: 'DONE' }),
    onSuccess: () => {
      toast.success('Task done');
      invalidate();
    },
    onError: () => toast.error('Could not update it', 'Try again in a moment.'),
  });

  const completeReminder = useMutation({
    mutationFn: (id: string) => api.post(`/workspace/reminders/${id}/done`),
    onSuccess: () => {
      toast.success('Reminder cleared');
      invalidate();
    },
    onError: () => toast.error('Could not clear it', 'Try again in a moment.'),
  });

  const readAll = useMutation({
    mutationFn: () => api.post('/internal/mails/read-all'),
    onSuccess: invalidate,
  });

  /**
   * What is actually waiting on this person.
   *
   * Shown on the collapsed strip, because hiding the column must not hide an
   * overdue reminder — a control that quietly buries work is worse than no
   * control at all.
   */
  const waiting =
    (data?.counts.dueReminders ?? 0) +
    (data?.counts.unreadMails ?? 0) +
    (data?.tasks.filter((t) => t.isOverdue).length ?? 0);

  if (collapsed) {
    return (
      /*
       * The whole strip expands, not just the icon.
       *
       * The first version made only the 16px button clickable, and a 44px
       * column that does nothing when you click it reads as dead space — or as
       * the panel having been deleted. Making the entire strip the control
       * removes that failure mode.
       */
      <aside
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(false);
          }
        }}
        aria-label="Show the internal update column"
        aria-expanded={false}
        title="Show internal update"
        className="group hidden w-[44px] shrink-0 cursor-pointer flex-col items-center gap-3 border-l border-white/5 bg-auth-panel py-4 transition-colors hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-auth-gold lg:flex"
      >
        <span className="relative grid h-8 w-8 place-items-center rounded-control text-white/50 transition-colors group-hover:text-white">
          <PanelRightOpen size={16} />
          {waiting > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-auth-gold px-1 text-[8px] font-bold text-auth-panel">
              {waiting > 9 ? '9+' : waiting}
            </span>
          )}
        </span>

        {/* Vertical label, so the strip reads as a collapsed panel rather than
            an unexplained sliver of dark. */}
        <span
          className="select-none font-mono text-[9px] uppercase tracking-[0.18em] text-white/30 transition-colors group-hover:text-white/60"
          style={{ writingMode: 'vertical-rl' }}
        >
          Internal update · show
        </span>
      </aside>
    );
  }

  return (
    // Appears from lg, because three columns are the layout — not a widescreen
    // luxury. It scales down instead of disappearing: 232px at lg, 264 at xl,
    // 300 at 2xl.
    <aside className="hidden w-[232px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-white/5 bg-auth-panel px-2.5 py-4 scrollbar-thin lg:flex xl:w-[264px] xl:px-3 2xl:w-[300px]">
      <div className="px-1">
        <div className="flex items-center gap-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
            Internal update
          </p>
          <button
            type="button"
            onClick={() => setPinging(true)}
            title="Ping a colleague"
            aria-label="Ping a colleague"
            className="ml-auto flex items-center gap-1 rounded-control px-1.5 py-1 text-[10px] font-semibold text-auth-gold transition-colors hover:bg-white/5"
          >
            <Zap size={11} />
            Ping
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Hide internal update"
            aria-label="Hide the internal update column"
            aria-expanded
            className="grid h-6 w-6 shrink-0 place-items-center rounded-control text-white/40 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold"
          >
            <PanelRightClose size={13} />
          </button>
        </div>
        {/* Says the quiet part out loud: this column does not follow the
            client chip above the working area. */}
        <p className="mt-1 flex items-center gap-1 text-[9.5px] leading-tight text-white/25">
          <ShieldCheck size={9} className="shrink-0" />
          Same for every client
        </p>
      </div>

      <RailCard
        title="Tasks"
        icon={<ListChecks size={13} />}
        count={data?.counts.myTasks}
        to="/internal/tasks"
        action={
          <button
            type="button"
            onClick={() => setAddingTask(true)}
            className="text-[11px] font-semibold text-auth-gold transition-colors hover:text-auth-gold/70"
          >
            Add
          </button>
        }
      >
        {isLoading ? (
          <RailSkeleton rows={4} />
        ) : !data || data.tasks.length === 0 ? (
          <RailEmpty>Nothing outstanding. Good place to be.</RailEmpty>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data.tasks.map((t) => (
              <li key={t.id} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => completeTask.mutate(t.id)}
                  aria-label={`Mark "${t.title}" done`}
                  className="mt-[3px] grid h-[14px] w-[14px] shrink-0 place-items-center rounded-full border border-white/25 text-transparent transition-colors hover:border-go hover:bg-go hover:text-white"
                >
                  <Check size={9} strokeWidth={3} />
                </button>
                <Link to="/internal/tasks" className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] leading-snug text-white/85 hover:text-white">
                    {t.title}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1.5 text-[10px]">
                    {t.dueAt && (
                      <span
                        className={cn(
                          t.isOverdue ? 'font-semibold text-danger-mid' : 'text-white/35',
                        )}
                      >
                        {t.isOverdue ? 'Overdue · ' : ''}
                        {relativeTime(new Date(t.dueAt))}
                      </span>
                    )}
                  </span>
                </Link>
                <span
                  className={cn(
                    'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                    PRIORITY_DOTS[t.priority],
                  )}
                  title={t.priority.toLowerCase()}
                />
              </li>
            ))}
          </ul>
        )}
      </RailCard>

      <RailCard
        title="Reminders"
        to="/internal/reminders"
        icon={<BellRing size={13} />}
        count={data?.counts.dueReminders}
        action={
          <button
            type="button"
            onClick={() => setAddingReminder(true)}
            className="text-[11px] font-semibold text-auth-gold transition-colors hover:text-auth-gold/70"
          >
            Set
          </button>
        }
      >
        {isLoading ? (
          <RailSkeleton rows={2} />
        ) : !data || data.reminders.length === 0 ? (
          <RailEmpty>Nothing set. Nudge yourself or a colleague.</RailEmpty>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data.reminders.map((r) => (
              <li key={r.id} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => completeReminder.mutate(r.id)}
                  aria-label={`Clear "${r.title}"`}
                  className="mt-[3px] grid h-[14px] w-[14px] shrink-0 place-items-center rounded-full border border-white/25 text-transparent transition-colors hover:border-go hover:bg-go hover:text-white"
                >
                  <Check size={9} strokeWidth={3} />
                </button>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] leading-snug text-white/85">
                    {r.title}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1.5 text-[10px]">
                    <span
                      className={cn(r.isDue ? 'font-semibold text-danger-mid' : 'text-white/35')}
                    >
                      {r.isDue ? 'Due · ' : ''}
                      {relativeTime(new Date(r.remindAt))}
                    </span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </RailCard>

      <RailCard
        title="Internal mails"
        icon={<Mail size={13} />}
        count={data?.counts.unreadMails}
        to="/internal/mails"
        action={
          data && data.counts.unreadMails > 0 ? (
            <button
              type="button"
              onClick={() => readAll.mutate()}
              className="text-[11px] font-semibold text-auth-gold transition-colors hover:text-auth-gold/70"
            >
              Mark read
            </button>
          ) : undefined
        }
      >
        {isLoading ? (
          <RailSkeleton rows={3} />
        ) : !data || data.mails.length === 0 ? (
          <RailEmpty>No internal updates.</RailEmpty>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.mails.map((n) => (
              <li key={n.id}>
                <Link to={n.link ?? '/internal/mails'} className="flex items-start gap-2">
                  <span
                    className={cn(
                      'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                      n.isRead ? 'bg-transparent' : 'bg-auth-gold',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-[12px] leading-snug',
                        n.isRead ? 'text-white/50' : 'font-semibold text-white/90',
                      )}
                    >
                      {n.title}
                    </span>
                    <span className="block truncate text-[10px] text-white/35">{n.body}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </RailCard>

      <RailCard
        title="Internal calendar"
        icon={<CalendarDays size={13} />}
        to="/internal/calendar"
      >
        {isLoading ? (
          <RailSkeleton rows={3} />
        ) : !data || data.events.length === 0 ? (
          <RailEmpty>Nothing booked internally.</RailEmpty>
        ) : (
          <ul className="mb-3 flex flex-col gap-1.5">
            {data.events.map((e) => (
              <li key={e.id}>
                <Link to="/internal/calendar" className="flex items-start gap-2">
                  <span className="tabular w-[52px] shrink-0 text-[11px] font-semibold text-white/85">
                    {formatIstTime(new Date(e.startsAt))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] leading-snug text-white/80">
                      {e.title}
                    </span>
                    {e.location && (
                      <span className="block truncate text-[10px] text-white/35">{e.location}</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {!isLoading && <MiniCalendar days={data?.calendarDays ?? []} />}
      </RailCard>

      <RailCard title="Notes" icon={<NotebookPen size={13} />} to="/internal/notes">
        {isLoading ? (
          <RailSkeleton rows={3} />
        ) : !data || data.notes.length === 0 ? (
          <RailEmpty>Nothing written down yet.</RailEmpty>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data.notes.map((n) => (
              <li key={n.id}>
                <Link to="/internal/notes" className="flex items-start gap-2">
                  {n.isPinned && <Pin size={9} className="mt-1 shrink-0 text-bee" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] leading-snug text-white/80">
                      {n.title}
                    </span>
                    <span className="block text-[10px] text-white/30">
                      {relativeTime(new Date(n.updatedAt))}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </RailCard>

      {/*
        Not a card: history has nothing live to show, and a card that is
        always empty trains people to ignore that column.
      */}
      <Link
        to='/internal/history'
        className='mt-1 flex items-center justify-center gap-1.5 rounded-lg border border-white/5 px-3 py-2 text-[11px] font-semibold text-white/40 transition-colors hover:border-white/10 hover:text-white/70'
      >
        <HistoryIcon size={11} />
        Completed history
      </Link>
      <TaskDialog open={addingTask} onClose={() => setAddingTask(false)} onSaved={invalidate} />
      <ReminderDialog
        open={addingReminder}
        onOpenChange={setAddingReminder}
        onSaved={invalidate}
      />
      <PingDialog open={pinging} onOpenChange={setPinging} />
    </aside>
  );
}

/** The whole card header is the link to its full page. */
function RailCard({
  title,
  icon,
  count,
  action,
  to,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  action?: React.ReactNode;
  to?: string;
  children: React.ReactNode;
}) {
  const heading = (
    <>
      <span className="text-white/40">{icon}</span>
      <h2 className="text-[12px] font-bold text-white/90">{title}</h2>
      {count !== undefined && count > 0 && (
        <span className="tabular grid h-[16px] min-w-[16px] place-items-center rounded-full bg-auth-gold px-1 text-[9px] font-bold text-auth-panel">
          {count}
        </span>
      )}
    </>
  );

  return (
    <section className="rounded-card border border-white/[0.07] bg-white/[0.05] px-3 py-3">
      <header className="mb-2 flex items-center gap-1.5">
        {to ? (
          <Link to={to} className="flex flex-1 items-center gap-1.5 hover:opacity-80">
            {heading}
          </Link>
        ) : (
          <span className="flex flex-1 items-center gap-1.5">{heading}</span>
        )}
        {action && <span className="shrink-0">{action}</span>}
      </header>
      {children}
    </section>
  );
}

function RailSkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-3 bg-white/10" />
      ))}
    </div>
  );
}

function RailEmpty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-[11px] text-white/35">{children}</p>;
}

/** Month grid with a dot on days that hold an internal event. */
function MiniCalendar({ days }: { days: string[] }) {
  const today = istToday();
  const [y, m] = today.split('-').map(Number);

  const first = new Date(Date.UTC(y!, m! - 1, 1));
  const startOffset = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  const marked = new Set(days);

  const cells: Array<number | null> = [
    ...Array.from({ length: startOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-center text-[9px] font-semibold text-white/30">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (day === null) return <span key={`pad-${i}`} />;
          const key = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = key === today;
          const hasEvent = marked.has(key);

          return (
            <span
              key={key}
              title={hasEvent ? 'Has internal events' : undefined}
              className={cn(
                'relative grid h-[22px] place-items-center rounded text-[10px]',
                isToday ? 'bg-auth-gold font-bold text-auth-panel' : 'text-white/55',
              )}
            >
              {day}
              {hasEvent && !isToday && (
                <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-auth-gold" />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Everyone who can be given internal work. Employees only. */
function useStaff(enabled: boolean) {
  return useQuery({
    queryKey: ['internal', 'staff'],
    queryFn: () => api.get<{ data: TeamMate[] }>('/internal/staff'),
    enabled,
  });
}

function TaskDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const staff = useStaff(open);

  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [dueAt, setDueAt] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api.post('/internal/tasks', {
        title: title.trim(),
        assigneeId: assigneeId || null,
        priority,
        dueAt: dueAt ? new Date(`${dueAt}T09:00:00`).toISOString() : null,
      }),
    onSuccess: () => {
      toast.success('Task created', 'It is on the internal board too.');
      setTitle('');
      setAssigneeId('');
      setDueAt('');
      onClose();
      onSaved();
    },
    onError: (err) =>
      toast.error(
        'Could not create it',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="New internal task"
      description="Appears here and on the internal board. No client sees it."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={save.isPending}
            disabled={title.trim().length === 0}
            onClick={() => save.mutate()}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Write the Q3 process doc"
        />
        <Select
          label="Assign to"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          options={[
            { value: '', label: 'Nobody yet' },
            ...(staff.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
          ]}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          options={PRIORITIES.map((p) => ({
            value: p,
            label: p.charAt(0) + p.slice(1).toLowerCase(),
          }))}
        />
        <Input label="Due" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
      </div>
    </Modal>
  );
}

function ReminderDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const staff = useStaff(open);

  const [title, setTitle] = useState('');
  const [forWhom, setForWhom] = useState('');
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api.post('/workspace/reminders', {
        title: title.trim(),
        userId: forWhom || null,
        note: note.trim() || null,
        remindAt: new Date(when).toISOString(),
      }),
    onSuccess: () => {
      toast.success(forWhom ? 'Reminder sent' : 'Reminder set');
      setTitle('');
      setWhen('');
      setForWhom('');
      setNote('');
      onOpenChange(false);
      onSaved();
    },
    onError: (err) =>
      toast.error(
        'Could not set it',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Set a reminder"
      description="For yourself, or for a colleague."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            isLoading={save.isPending}
            disabled={title.trim().length === 0 || !when}
            onClick={() => save.mutate()}
          >
            Set reminder
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label="Remind about"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Chase the AMC quotation"
        />
        <Select
          label="For"
          value={forWhom}
          onChange={(e) => setForWhom(e.target.value)}
          options={[
            { value: '', label: 'Me' },
            ...(staff.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
          ]}
          hint={forWhom ? 'They are told now, and again at the time.' : undefined}
        />
        <Input
          label="When"
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
        <Textarea
          label="Note"
          rows={3}
          value={note}
          maxLength={600}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything worth remembering when this comes up"
          hint="Optional. Shown with the reminder and kept in your history."
        />
      </div>
    </Modal>
  );
}

function PingDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const staff = useStaff(open);
  const [toId, setToId] = useState('');
  const [message, setMessage] = useState('');

  const send = useMutation({
    mutationFn: () => api.post(`/internal/ping/${toId}`, { message: message.trim() }),
    onSuccess: () => {
      toast.success('Ping sent');
      setMessage('');
      setToId('');
      onOpenChange(false);
    },
    onError: (err) =>
      toast.error(
        'Could not send it',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Ping a colleague"
      description="Lands in their internal mail straight away."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            isLoading={send.isPending}
            disabled={!toId || message.trim().length === 0}
            onClick={() => send.mutate()}
          >
            Send ping
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Select
          label="To"
          value={toId}
          onChange={(e) => setToId(e.target.value)}
          options={[
            { value: '', label: 'Choose someone' },
            ...(staff.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
          ]}
        />
        <Textarea
          label="Message"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Can you look at the Shree Textiles invoice before 5?"
        />
      </div>
    </Modal>
  );
}
